import { createRoot, type Root } from 'react-dom/client';
import type { ReactElement } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   Montage et DÉMONTAGE de l'arbre React.

   La deuxième opération est la plus importante des deux (PLAN.md §3.7).
   Entrer en partie appelle `unmount()` : plus aucun composant, plus aucun
   abonnement, plus aucun `setState` possible pendant les 16,67 ms qui comptent.

   Le vrai piège que cela évite n'est pas le coût de React au repos — il est
   négligeable — mais la tentation d'écrire dans un état de framework depuis
   `requestAnimationFrame`. Quand l'arbre n'existe pas, la tentation n'existe
   pas non plus : c'est une contrainte structurelle, pas une discipline.
   ═══════════════════════════════════════════════════════════════════════════ */

export class UiRoot {
  private readonly host: HTMLElement;
  private root: Root | null = null;

  constructor(parent: HTMLElement = document.body) {
    const host = document.createElement('div');
    host.id = 'ui-root';
    parent.appendChild(host);
    this.host = host;
  }

  get mounted(): boolean {
    return this.root !== null;
  }

  /** Le conteneur DOM persistant de l'arbre (il survit aux re-rendus). Exposé
   *  pour brancher les sons d'interface par délégation, voir `ui/uiSfx.ts`. */
  get container(): HTMLElement {
    return this.host;
  }

  /** Voile de l'attract mode : le menu passe à 25 % d'opacité et cesse de
   *  répondre au pointeur, pour laisser voir la démo qui tourne derrière sans
   *  qu'un clic ne l'active par accident (c'est un geste global qui la quitte,
   *  géré dans `main.ts`). Simple bascule de classe : aucune reconstruction de
   *  l'arbre, donc aucun coût. */
  setDimmed(on: boolean): void {
    this.host.classList.toggle('attract-dim', on);
  }

  render(node: ReactElement): void {
    if (!this.root) this.root = createRoot(this.host);
    this.root.render(node);
  }

  /** ⚠️ Démontage RÉEL, pas un simple `render(null)`.
   *  Rendre `null` laisserait le root vivant, ses abonnements en place et son
   *  planificateur actif — donc exactement ce qu'on cherche à supprimer. */
  unmount(): void {
    if (!this.root) return;
    this.root.unmount();
    this.root = null;
    this.host.replaceChildren();
  }
}
